<script setup lang="ts">
import type { Category } from '@/types/Product';
import { AppTypography } from '../base';

const { isMobile = false, topCategories = false } = defineProps<{
  isMobile?: boolean;
  topCategories?: boolean;
}>();

const { data: categories, execute: getCategories, pending } = useGetData<{ data: Category[] }>();
pending.value = true;

const allCategories = computed(() => {
  return categories.value?.data || [];
});

const filteredTopCategories = computed(() => {
  const top = ['Automotive', 'Building', 'Plumbing', 'Electrical', 'Tools'];

  if (!topCategories) return allCategories.value;

  const topLower = top.map((t) => t.toLowerCase());

  return allCategories.value
    .filter((category) => topLower.includes(category.label.toLowerCase()))
    .sort((a, b) => topLower.indexOf(a.label.toLowerCase()) - topLower.indexOf(b.label.toLowerCase()));
});

onMounted(() => {
  getCategories('/categories?filter[type]=product');
});
</script>

<template>
  <div class="products">
    <AppTypography v-if="isMobile" class="mb-3" element="h1" type="title-3">Products</AppTypography>

    <!-- Skeleton -->
    <div v-if="pending" class="categories">
      <v-skeleton-loader type="chip@15"></v-skeleton-loader>
    </div>

    <div v-if="!pending" class="categories">
      <v-chip-group :column="isMobile" :show-arrows="!isMobile">
        <v-chip
          v-for="category in filteredTopCategories"
          class="category-chip"
          variant="outlined"
          size="x-large"
          link
          :key="category.id"
          :to="`/browse/categories/${category.id}`"
        >
          <AppTypography element="span" type="subheadline-1" color="-tertiary" class="chip-label text-truncate">
            {{ category.label }}
          </AppTypography>
        </v-chip>

        <v-chip v-if="topCategories" class="category-chip" variant="outlined" size="x-large">
          <AppTypography element="span" type="subheadline-1" color="-tertiary" class="chip-label text-truncate">
            All Categories <v-icon icon="mdi-chevron-down"></v-icon>
          </AppTypography>

          <v-menu activator="parent" height="300" width="300">
            <v-list>
              <v-list-item
                v-for="(category, index) in allCategories"
                :key="index"
                :value="index"
                :to="`/browse/categories/${category.id}`"
                link
              >
                <v-list-item-title>{{ category.label }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-chip>
      </v-chip-group>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.products {

  @media (max-width: 768px) {
    padding-inline: 16px;
    padding-bottom: 16px;
  }

  :deep(.v-slide-group__content) {
    gap: 8px !important;
  }

  .categories {
    :deep(.v-skeleton-loader) {
      gap: 8px;
      margin-top: 12px;
      padding: 4px;
      flex-wrap: nowrap;
      overflow: hidden;

      @media (max-width: 768px) {
        flex-wrap: wrap;
        overflow: auto;
      }

      .v-skeleton-loader__chip {
        margin: 0;
        min-height: 44px;
        border-radius: 9999px;
        min-width: 90px;

        &:nth-of-type(2n) {
          min-width: 170px;
        }
        &:nth-of-type(3n + 1) {
          min-width: 130px;
        }
        &:nth-of-type(5n-2) {
          min-width: 100px;
        }
      }
    }
  }

  .category-chip {
    border-color: rgb(var(--v-theme-stroke));
    margin: 0px;

    .chip-label {
      max-width: 300px;
    }
  }
}
</style>
